import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Pressable,
  Animated,
  ActivityIndicator,
  RefreshControl,
  Switch,
  Alert,
  Modal,
  Linking,
  TextInput,
  Platform,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@constants/colors';
import { FormattedMessage } from '@components/chat/FormattedMessage';
import { Spacing, Radius } from '@constants/spacing';
import { Typography } from '@constants/typography';
import { Elevation } from '@constants/elevation';
import { SearchBar } from '@components/home/SearchBar';
import { StockHistoryDropdown } from '@components/home/StockHistoryDropdown';
import { MarketCard } from '@components/market/MarketCard';
import { StockChart } from '@components/charts/StockChart';
import { MarketStatusBadge } from '@components/market/MarketStatusBadge';
import { CollapsibleSection } from '@components/common/CollapsibleSection';
import { SwipableMarketMovers } from '@components/market/SwipableMarketMovers';
import { NewsSnippets } from '@components/news/NewsSnippets';
import { UpcomingEventsSnippets } from '@components/events/UpcomingEventsSnippets';
import { MiniSparkline } from '@components/charts/MiniSparkline';
import { perplexityService } from '@services/api/perplexity.service';
import { useMarketNews } from '@hooks/useMarketNews';
import { stockDataService } from '@services/api/stockData.service';
import { polygonMarketDataService } from '@services/api/polygonMarketData.service';
import {
  saveSearchHistory,
  getLatestStockSearch,
  appendMessageToHistory,
  getFullStockConversationHistory,
} from '@services/history/searchHistoryService';
import { useAuthStore } from '@store/authStore';
import { supabase } from '@services/supabase/supabaseClient';
import { Button } from '@components/common/Button';
import { PlaidLinkButton } from '@components/PlaidLinkButton';
import { extractStockSymbol } from '@utils/stockSymbolExtractor';
import { tickerDetectionService } from '@services/stock/tickerDetectionService';
import {
  getPortfolioConnection,
  getPortfolioConnections,
  refreshHoldings,
  disconnectPortfolio,
  getPortfolioPositions,
} from '@services/plaid/plaidService';
import { useMarketData, MarketData } from '@hooks/useMarketData';
import { useWatchlist } from '@hooks/useWatchlist';
import { useRecentlyViewed } from '@hooks/useRecentlyViewed';
import { useMarketMovers } from '@hooks/useMarketMovers';
import { useStockSparklines } from '@hooks/useStockSparklines';
import { useResponsive } from '@hooks/useResponsive';
import { useMacroForMe } from '@hooks/useMacroForMe';
import { useThemesRadar } from '@hooks/useThemesRadar';
import { useThemeStories } from '@hooks/useThemeStories';
import { StockDataResponse } from '../types/stock.types';
import { StockSearchSummary, Message } from '../types/search-history.types';
import { ENV } from '@/config/env';
import { Video, ResizeMode } from 'expo-av';
import {
  createThread as createHomeThread,
  appendMessages as appendHomeMessages,
  listThreads as listHomeThreads,
  getMessages as getHomeThreadMessages,
  updateSummary as updateHomeThreadSummary,
} from '@services/chat/homeThreadService';

interface HomeScreenProps {
  navigation?: any;
}

function MinimalHome({ navigation }: { navigation?: any }) {
  const [messages, setMessages] = React.useState<
    Array<{
      role: 'user' | 'assistant';
      content: string;
      createdAt: number;
      detectedTicker?: string;
      companyName?: string;
      suggestedTickers?: string[];
    }>
  >([]);
  const [loading, setLoading] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [stockOpen, setStockOpen] = React.useState(false);
  const [stockInput, setStockInput] = React.useState('');
  const [detecting, setDetecting] = React.useState(false);
  const [detectError, setDetectError] = React.useState<string | null>(null);
  const [detectSuggestions, setDetectSuggestions] = React.useState<
    Array<{ symbol: string; name: string }>
  >([]);
  const [threads, setThreads] = React.useState<
    Array<{
      id: string;
      title: string;
      updatedAt: number;
      messages: any[];
      summary?: string;
      boundTicker?: string;
      companyName?: string;
      tickerNotes?: string;
      serverId?: string;
    }>
  >([]);
  const [serverThreads, setServerThreads] = React.useState<
    Array<{ id: string; title: string; updated_at: string; bound_ticker?: string | null }>
  >([]);
  const [serverThreadsLoading, setServerThreadsLoading] = React.useState(false);
  const [threadLoadError, setThreadLoadError] = React.useState<string | null>(null);
  const [currentThreadId, setCurrentThreadId] = React.useState<string | null>(null);
  const { recentlyViewed, addRecentlyViewed } = useRecentlyViewed();
  const { watchlist } = useWatchlist?.() || ({ watchlist: [] } as any);

  const { user } = useAuthStore();
  const THREADS_KEY = React.useMemo(() => `tp_home_threads_v1:${user?.id || 'anon'}`, [user?.id]);

  React.useEffect(() => {
    console.log('📦 [LocalStorage] Loading threads from:', THREADS_KEY);
    console.log('📦 [LocalStorage] User ID:', user?.id || 'anon');
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(THREADS_KEY) : null;
      console.log('📦 [LocalStorage] Raw data exists:', !!raw);
      if (raw) {
        const parsed = JSON.parse(raw);
        console.log(
          '📦 [LocalStorage] Parsed threads count:',
          Array.isArray(parsed) ? parsed.length : 0
        );
        if (Array.isArray(parsed)) {
          setThreads(parsed);
          console.log('✅ [LocalStorage] Loaded', parsed.length, 'threads');
        } else {
          setThreads([]);
          console.log('⚠️ [LocalStorage] Data is not an array');
        }
      } else {
        setThreads([]);
        console.log('📦 [LocalStorage] No threads found, starting fresh');
      }
    } catch (error) {
      console.error('❌ [LocalStorage] Parse error:', error);
      setThreads([]);
    }
  }, [THREADS_KEY, user?.id]);

  const persistThreads = React.useCallback(
    (next: any[]) => {
      setThreads(next);
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(THREADS_KEY, JSON.stringify(next.slice(0, 10)));
        }
      } catch {}
    },
    [THREADS_KEY]
  );

  // Disabled: Auto-restore last thread on Home load
  // Intentional: Show default page until the user picks a thread or sends a new message
  // If we want to re-enable optionally, gate with a runtime flag here.
  // React.useEffect(() => {
  //   const ENABLE_AUTO_RESTORE = false;
  //   if (!ENABLE_AUTO_RESTORE) return;
  //   if (threads.length > 0 && !currentThreadId) {
  //     const mostRecent = threads.slice().sort((a, b) => b.updatedAt - a.updatedAt)[0];
  //     setCurrentThreadId(mostRecent.id);
  //     setMessages(mostRecent.messages || []);
  //   }
  // }, [threads, currentThreadId]);

  const ensureThread = React.useCallback(
    (firstUserText: string) => {
      console.log('🧵 [EnsureThread] Called with:', firstUserText.slice(0, 30) + '...');
      console.log('🧵 [EnsureThread] currentThreadId:', currentThreadId);

      if (currentThreadId) {
        console.log('✅ [EnsureThread] Reusing existing thread:', currentThreadId);
        return currentThreadId;
      }

      const id = String(Date.now());
      console.log('🆕 [EnsureThread] Creating NEW thread:', id);
      const title = firstUserText.slice(0, 80);
      const entry = { id, title, updatedAt: Date.now(), messages: [] };
      persistThreads([entry, ...threads].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 10));
      setCurrentThreadId(id);
      console.log('🧵 [EnsureThread] Thread created and set as current');
      return id;
    },
    [currentThreadId, threads, persistThreads]
  );

  const updateThreadById = React.useCallback(
    (id: string, updater: (t: any) => any) => {
      const next = threads.map(t => (t.id === id ? updater({ ...t }) : t));
      persistThreads(next);
    },
    [threads, persistThreads]
  );

  const updateCurrentThread = React.useCallback(
    (updater: (t: any) => any) => {
      if (!currentThreadId) return;
      const next = threads.map(t => (t.id === currentThreadId ? updater({ ...t }) : t));
      persistThreads(next);
    },
    [currentThreadId, threads, persistThreads]
  );

  const handleOpenThread = async (id: string) => {
    // Try to find local thread first
    const localThread = threads.find(x => x.id === id);

    if (localThread) {
      // Load from local state
      setCurrentThreadId(id);
      setMessages(localThread.messages || []);
      setHistoryOpen(false);
      return;
    }

    // If not found locally, it's a server-only thread - fetch messages
    const serverThread = serverThreads.find(x => x.id === id);
    if (!serverThread) return;

    try {
      setLoading(true);
      const res = await getHomeThreadMessages(id);

      if (res.success && res.messages) {
        // Convert server messages to local format
        const messages = res.messages.map(m => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
          createdAt: new Date(m.created_at).getTime(),
        }));

        // Create local thread from server data
        const newThread = {
          id: serverThread.id,
          title: serverThread.title,
          updatedAt: new Date(serverThread.updated_at).getTime(),
          messages,
          serverId: serverThread.id,
          boundTicker: serverThread.bound_ticker || undefined,
        };

        // Add to local state
        persistThreads([newThread, ...threads]);
        setCurrentThreadId(serverThread.id);
        setMessages(messages);
      }

      setHistoryOpen(false);
    } catch (error) {
      console.error('Failed to load server thread messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load server threads when history opens
  React.useEffect(() => {
    if (!historyOpen) {
      console.log('🔄 [ServerThreads] History modal not open, skipping load');
      return;
    }

    console.log('🔄 [ServerThreads] History modal opened, loading server threads...');
    console.log('🔄 [ServerThreads] User authenticated:', !!user);
    console.log('🔄 [ServerThreads] User ID:', user?.id);

    (async () => {
      setServerThreadsLoading(true);
      setThreadLoadError(null);
      try {
        console.log('📡 [ServerThreads] Calling listHomeThreads(20)...');
        const res = await listHomeThreads(20);
        console.log('📡 [ServerThreads] Response:', {
          success: res.success,
          threadCount: res.threads?.length || 0,
          error: res.error,
        });

        if (res.success && res.threads) {
          const mappedThreads = res.threads.map(t => ({
            id: t.id,
            title: t.title,
            updated_at: t.updated_at,
            bound_ticker: t.bound_ticker,
          }));
          setServerThreads(mappedThreads);
          console.log('✅ [ServerThreads] Loaded', mappedThreads.length, 'server threads');
        } else if (!res.success) {
          const errorMsg = res.error || 'Failed to load conversations';
          console.error('❌ [ServerThreads] Load failed:', errorMsg);
          setThreadLoadError(errorMsg);
        } else {
          console.log('📡 [ServerThreads] No threads returned from server');
          setServerThreads([]);
        }
      } catch (error) {
        console.error('❌ [ServerThreads] Exception:', error);
        setThreadLoadError(error instanceof Error ? error.message : 'Unknown error');
      } finally {
        setServerThreadsLoading(false);
        console.log('🔄 [ServerThreads] Loading complete');
      }
    })();
  }, [historyOpen, user]);

  // Log modal visibility changes for debugging
  React.useEffect(() => {
    console.log('🎭 [Modal] historyOpen changed to:', historyOpen);
  }, [historyOpen]);

  React.useEffect(() => {
    console.log('🎭 [Modal] stockOpen changed to:', stockOpen);
  }, [stockOpen]);

  // Merge local and server threads for unified display
  const mergedThreads = React.useMemo(() => {
    const threadMap = new Map();

    // Add local threads first
    threads.forEach(t => {
      threadMap.set(t.id, {
        id: t.id,
        title: t.title,
        updatedAt: t.updatedAt,
        serverId: t.serverId,
        source: 'local' as const,
        localThread: t,
      });
    });

    // Add or merge server threads
    serverThreads.forEach(st => {
      // Check if this server thread is already represented by a local thread
      const matchingLocal = threads.find(t => t.serverId === st.id);

      if (matchingLocal) {
        // Update the existing entry with server ID
        threadMap.set(matchingLocal.id, {
          id: matchingLocal.id,
          title: matchingLocal.title,
          updatedAt: matchingLocal.updatedAt,
          serverId: st.id,
          source: 'both' as const,
          localThread: matchingLocal,
          serverThread: st,
        });
      } else {
        // Add server-only thread
        threadMap.set(st.id, {
          id: st.id,
          title: st.title,
          updatedAt: new Date(st.updated_at).getTime(),
          serverId: st.id,
          source: 'server' as const,
          serverThread: st,
        });
      }
    });

    // Convert to array and sort by most recent
    const merged = Array.from(threadMap.values()).sort((a, b) => b.updatedAt - a.updatedAt);
    console.log(
      '📊 [MergedThreads] Local:',
      threads.length,
      'Server:',
      serverThreads.length,
      'Merged:',
      merged.length
    );
    return merged;
  }, [threads, serverThreads]);

  async function summarizeThread(t: any) {
    try {
      const base = t.summary || '';
      const lastTurns = (t.messages || [])
        .slice(-8)
        .map((m: any) => `${m.role}: ${m.content}`)
        .join('\n');
      const prompt = `Summarize the conversation for a financial assistant in ≤150 words. Include tickers, stances, unresolved questions.\nPrevious summary:\n${base}\n\nNew turns:\n${lastTurns}`;
      const resp = await perplexityService.askQuestion(prompt, 'week' as any);
      return resp?.data?.content || base;
    } catch {
      return null;
    }
  }

  const handleSearch = async (text: string) => {
    const query = (text || '').trim();
    if (!query) return;

    const tid = ensureThread(query);

    const userMsg = { role: 'user' as const, content: query, createdAt: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    updateThreadById(tid, t => ({
      ...t,
      messages: [...(t.messages || []), userMsg],
      updatedAt: Date.now(),
    }));

    setLoading(true);
    try {
      // build summary + full conversation context
      const threadSnapshot = (threads.find(x => x.id === tid) as any) || {};
      // Send full conversation history to AI (thread summary provides high-level overview)
      const allPrior = [...(threadSnapshot.messages || []), userMsg];
      const allMessages = allPrior
        .filter((m: any) => {
          const trimmed = (m?.content || '').trim();
          // Keep all assistant responses; drop only ultra-short user noise
          return m?.role === 'assistant' || trimmed.length > 5;
        })
        .map((m: any) => ({ role: m.role, content: (m.content || '').trim() }));

      const sysPrompt = `You are Grok, a sharp financial markets assistant built by xAI. DOMAIN: Financial markets only (stocks, indices, sectors, macro). Never answer unrelated shopping or consumer product questions; interpret ambiguous asks (e.g., "what should I buy") as investment positioning and propose tickers/sectors. Respond factually, concisely (<300 words), and insightfully—like a quant trader crossed with a witty analyst. Prioritize actionable intel: prices, catalysts, risks, sentiment. ALWAYS reference prior context if relevant to continue the conversation thread.\n\nStructure responses:\n- **Key Facts**: Bullet 2-3 headlines (e.g., price/change, major news).\n- **Analysis**: 1-2 para reasoning (step-by-step if complex). Tie to conversation history.\n- **Next?**: 1-2 smart follow-up questions.\n\nIf no ticker detected, suggest 2-3 relevant ones with why (tickers only). Use tables for comparisons where helpful. Be truthful—flag uncertainties. ${threadSnapshot.summary ? `Summary: ${threadSnapshot.summary}. ` : ''}${threadSnapshot.boundTicker ? `Focus: ${threadSnapshot.boundTicker}${threadSnapshot.tickerNotes ? ` (${threadSnapshot.tickerNotes})` : ''}. ` : ''}`;
      const sysBlocks: Array<{ role: 'system'; content: string }> = [
        { role: 'system', content: sysPrompt },
      ];
      if (Array.isArray(watchlist) && watchlist.length) {
        sysBlocks.push({
          role: 'system',
          content: `User watchlist: ${watchlist
            .slice(0, 8)
            .map((w: any) => w.ticker || w.symbol || '')
            .filter(Boolean)
            .join(', ')}`,
        });
      }
      if (Array.isArray(recentlyViewed) && recentlyViewed.length) {
        sysBlocks.push({
          role: 'system',
          content: `Recently viewed: ${recentlyViewed
            .slice(0, 6)
            .map((rv: any) => rv.ticker)
            .join(', ')}`,
        });
      }

      // If user used pronouns (it/they/this/that) and we have a bound ticker, clarify the last user turn inline
      // Try boundTicker first, but fall back to detecting from recent messages if state is stale
      let focusTicker: string | undefined = threadSnapshot.boundTicker;
      let focusCompany: string | undefined =
        threadSnapshot.companyName || threadSnapshot.boundTicker;

      // Fallback: If no boundTicker (state might be stale), check recent assistant messages for detectedTicker
      if (!focusTicker && threadSnapshot.messages && threadSnapshot.messages.length > 0) {
        const recentAssistant = [...threadSnapshot.messages]
          .reverse()
          .find((m: any) => m.role === 'assistant' && m.detectedTicker);
        if (recentAssistant) {
          focusTicker = recentAssistant.detectedTicker;
          focusCompany = recentAssistant.companyName || recentAssistant.detectedTicker;
          console.log('✅ [Pronoun Fallback] Using ticker from recent message:', focusTicker);
        }
      }

      console.log('🔍 [Pronoun] Focus ticker:', focusTicker, 'Company:', focusCompany);
      console.log('🔍 [Pronoun] Thread snapshot:', {
        boundTicker: threadSnapshot.boundTicker,
        companyName: threadSnapshot.companyName,
        messageCount: threadSnapshot.messages?.length,
      });

      const pronounPattern = /\b(it|they|this|that)\b/i;
      const mentionsFocus = (text: string, ticker?: string, name?: string) => {
        if (!text) return false;
        const t = ticker ? new RegExp(`\\b${ticker}\\b`, 'i') : null;
        const n = name
          ? new RegExp(`\\b${name.replace(/[-/\\^$*+?.()|[\]{}]/g, '')}\\b`, 'i')
          : null;
        return (t ? t.test(text) : false) || (n ? n.test(text) : false);
      };

      // Clone allMessages to avoid mutating the original reference
      const clarifiedLastK = [...allMessages];
      if (
        focusTicker &&
        clarifiedLastK.length > 0 &&
        clarifiedLastK[clarifiedLastK.length - 1].role === 'user'
      ) {
        const lastUser = clarifiedLastK[clarifiedLastK.length - 1];
        const content = lastUser.content || '';
        const lacksFocusMention = !mentionsFocus(content, focusTicker, focusCompany);
        const hasPronoun = pronounPattern.test(content) || content.trim().length <= 6;

        console.log('🔍 [Pronoun] User message:', content);
        console.log(
          '🔍 [Pronoun] Has pronoun:',
          hasPronoun,
          'Lacks focus mention:',
          lacksFocusMention
        );

        if (lacksFocusMention && hasPronoun) {
          const newContent = `Regarding ${focusTicker}${focusCompany ? ` (${focusCompany})` : ''}: ${content}`;
          console.log('✅ [Pronoun] Prepending context:', newContent);
          lastUser.content = newContent;
        } else {
          console.log('❌ [Pronoun] Not prepending - conditions not met');
        }
      } else {
        console.log('❌ [Pronoun] Skipped - no focusTicker or invalid message state');
      }

      const chatMessages = [...sysBlocks, ...clarifiedLastK];

      const isNews = /\b(today|now|latest|breaking|this (week|month)|headlines)\b/i.test(query);
      const recency = (isNews ? 'day' : 'week') as any;
      const [answer, detection] = await Promise.all([
        perplexityService.chat(chatMessages as any, recency),
        tickerDetectionService.detectTicker(query),
      ]);

      console.log('🔍 [Ticker Detection] Query:', query);
      console.log('🔍 [Ticker Detection] Result:', detection);

      let assistantMsg = {
        role: 'assistant' as const,
        content:
          (answer && (answer as any).data?.content) ||
          (typeof (answer as any) === 'string' ? (answer as any) : 'I found some info.'),
        createdAt: Date.now(),
        detectedTicker:
          detection?.ticker && detection.confidence >= 0.7 ? detection.ticker : undefined,
        companyName: detection?.companyName,
        suggestedTickers: [] as string[],
      };

      console.log(
        '🔍 [Assistant Msg] Detected ticker:',
        assistantMsg.detectedTicker,
        'Company:',
        assistantMsg.companyName
      );

      // Enhance with suggested tickers parsed from response (client-side, zero-cost)
      try {
        const tickerMatches = (assistantMsg.content.match(/\b[A-Z]{2,5}\b/g) || []) as string[];
        const uniqueTickers = Array.from(new Set(tickerMatches)).filter(
          t => t.length >= 2 && t.length <= 5
        );
        if (uniqueTickers.length > 0 && !assistantMsg.detectedTicker) {
          assistantMsg.suggestedTickers = uniqueTickers.slice(0, 3);
        }
      } catch {}

      // Update thread state FIRST (before displaying message) to avoid race condition
      // This ensures boundTicker is available for the next message's pronoun resolution
      updateThreadById(tid, t => {
        let boundTicker = t.boundTicker;
        let companyName = t.companyName;
        if (!boundTicker && assistantMsg.detectedTicker) {
          boundTicker = assistantMsg.detectedTicker;
          companyName = assistantMsg.companyName || assistantMsg.detectedTicker;
          console.log(
            '✅ [Thread Update] Setting boundTicker:',
            boundTicker,
            'companyName:',
            companyName
          );
        }
        return {
          ...t,
          boundTicker,
          companyName,
          messages: [...(t.messages || []), assistantMsg],
          updatedAt: Date.now(),
        };
      });

      // Display message AFTER thread state is updated
      setMessages(prev => [...prev, assistantMsg]);

      // Create server thread if missing, then append both messages
      if (user?.id) {
        try {
          let after = threads.find(x => x.id === tid) as any;
          let serverId = after?.serverId;
          if (!serverId) {
            const created = await createHomeThread(
              after?.title || query.slice(0, 80),
              after?.boundTicker
            );
            if (created.success && created.id) {
              serverId = created.id;
              updateThreadById(tid, t => ({ ...t, serverId }));
              after = { ...after, serverId };
            }
          }
          if (serverId) {
            await appendHomeMessages(serverId, [
              { role: 'user', content: userMsg.content },
              { role: 'assistant', content: assistantMsg.content },
            ]);
          }
        } catch {}
      }

      // persist stock history (create on first bind, append otherwise)
      const pre = threadSnapshot;
      const bound = pre?.boundTicker || assistantMsg.detectedTicker;
      const boundName = pre?.companyName || assistantMsg.companyName;
      if (bound) {
        if (!pre?.boundTicker && assistantMsg.detectedTicker) {
          await saveSearchHistory({
            query,
            stock_symbol: bound,
            stock_name: boundName,
            messages: [...messages, userMsg, assistantMsg] as any,
          });
        } else {
          await appendMessageToHistory(bound, {
            id: String(userMsg.createdAt),
            role: 'user',
            content: userMsg.content,
            timestamp: new Date(userMsg.createdAt).toISOString(),
          });
          await appendMessageToHistory(bound, {
            id: String(Date.now()),
            role: 'assistant',
            content: assistantMsg.content,
            timestamp: new Date().toISOString(),
          });
        }
      }

      // opportunistic summary refresh
      const latest = threads.find(x => x.id === tid);
      if ((latest?.messages?.length || 0) >= 6) {
        const s = await summarizeThread(latest);
        if (s) {
          updateThreadById(tid, t => ({ ...t, summary: s }));
          const srvId = (threads.find(x => x.id === tid) as any)?.serverId;
          if (srvId) {
            try {
              await updateHomeThreadSummary(srvId, s);
            } catch {}
          }
        }
      }
    } catch (e) {
      const errMsg = {
        role: 'assistant' as const,
        content: 'Sorry, something went wrong. Please try again.',
        createdAt: Date.now(),
      };
      setMessages(prev => [...prev, errMsg]);
      updateThreadById(tid, t => ({
        ...t,
        messages: [...(t.messages || []), errMsg],
        updatedAt: Date.now(),
      }));
    } finally {
      setLoading(false);
    }
  };

  const renderMessage = (m: any, idx: number) => {
    const isUser = m.role === 'user';
    return (
      <View
        key={idx}
        style={{
          paddingHorizontal: 20,
          paddingVertical: 10,
          alignItems: isUser ? 'flex-end' : 'flex-start',
        }}
      >
        <View
          style={{
            maxWidth: '90%',
            backgroundColor: isUser ? Colors.ai.primary : Colors.surface.base,
            borderWidth: 1,
            borderColor: isUser ? Colors.ai.primary : Colors.border.subtle,
            borderRadius: 12,
            padding: 12,
          }}
        >
          {isUser ? (
            <Text style={{ color: Colors.background.primary, fontSize: 14 }}>{m.content}</Text>
          ) : (
            <FormattedMessage content={m.content} />
          )}
          {!isUser && m.detectedTicker ? (
            <Pressable
              onPress={() => {
                addRecentlyViewed(m.detectedTicker, m.companyName || m.detectedTicker);
                navigation?.navigate('StockAnalysis', {
                  ticker: m.detectedTicker,
                  companyName: m.companyName,
                  sourceType: 'recent',
                  initialTab: 'chat',
                  initialQuery: messages?.find((mm: any) => mm.role === 'user')?.content || '',
                });
              }}
              style={{
                marginTop: 10,
                alignSelf: 'flex-start',
                paddingVertical: 8,
                paddingHorizontal: 10,
                borderRadius: 8,
                backgroundColor: Colors.background.secondary,
                borderWidth: 1,
                borderColor: Colors.border.subtle,
              }}
            >
              <Text style={{ color: Colors.ai.primary, fontWeight: '600' }}>
                Open {m.detectedTicker} analysis
              </Text>
            </Pressable>
          ) : null}
          {!isUser && Array.isArray(m.suggestedTickers) && m.suggestedTickers.length > 0 ? (
            <View style={{ marginTop: 8, flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {m.suggestedTickers.map((ticker: string) => (
                <Pressable
                  key={ticker}
                  onPress={() => {
                    addRecentlyViewed(ticker, ticker);
                    navigation?.navigate('StockAnalysis', {
                      ticker,
                      companyName: ticker,
                      sourceType: 'chat-suggest',
                    });
                  }}
                  style={{
                    paddingVertical: 4,
                    paddingHorizontal: 8,
                    borderRadius: 6,
                    backgroundColor: Colors.background.tertiary,
                    borderWidth: 1,
                    borderColor: Colors.border.subtle,
                  }}
                >
                  <Text style={{ color: Colors.ai.primary, fontSize: 12, fontWeight: '600' }}>
                    {ticker}
                  </Text>
                </Pressable>
              ))}
            </View>
          ) : null}
        </View>
      </View>
    );
  };

  const isEmpty = messages.length === 0 && !loading;
  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: Colors.background.primary }}>
      <StatusBar barStyle="light-content" />
      {!isEmpty && (
        <View
          style={{
            paddingHorizontal: 20,
            paddingTop: 12,
            paddingBottom: 8,
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text style={{ ...Typography.headline, fontSize: 20, color: Colors.text.primary }}>
            Chat
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Pressable
              onPress={() => {
                console.log('🔘 [Button] Stock Analysis button clicked (header)');
                setStockOpen(true);
              }}
              style={{ padding: 6, marginRight: 4 }}
              accessibilityLabel="Open stock analysis"
            >
              <Ionicons name="trending-up" size={22} color={Colors.text.secondary} />
            </Pressable>
            <Pressable
              onPress={() => {
                console.log('🔘 [Button] History button clicked (header)');
                setHistoryOpen(true);
              }}
              style={{ padding: 6 }}
              accessibilityLabel="Open history"
            >
              <Ionicons name="time-outline" size={22} color={Colors.text.secondary} />
            </Pressable>
          </View>
        </View>
      )}

      {isEmpty ? (
        <View
          style={{ flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 24 }}
        >
          {/* Centered brand */}
          <View style={{ alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
            <Image
              source={require('../../assets/Ticker Vibes.png')}
              style={{ width: 500, height: 240, borderRadius: 20 }}
              resizeMode="contain"
            />
          </View>
          {/* Centered composer */}
          <View style={{ width: '100%', maxWidth: 900 }}>
            <SearchBar
              placeholder="Ask anything about markets or stocks..."
              onSearch={handleSearch}
              onVoicePress={undefined}
            />
          </View>
          {/* Quick actions under composer */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              marginTop: 8,
            }}
          >
            <Pressable
              onPress={() => {
                console.log('🔘 [Button] Stock Analysis button clicked (empty state)');
                setStockOpen(true);
              }}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                backgroundColor: Colors.background.secondary,
                borderWidth: 1,
                borderColor: Colors.border.subtle,
                borderRadius: 999,
                paddingVertical: 8,
                paddingHorizontal: 12,
              }}
              accessibilityLabel="Open stock analysis"
            >
              <Ionicons name="trending-up" size={18} color={Colors.text.secondary} />
              <Text style={{ color: Colors.text.primary, fontSize: 13, fontWeight: '600' }}>
                Stock Analysis
              </Text>
            </Pressable>
            <Pressable
              onPress={() => {
                console.log('🆕 [Button] New chat clicked (empty state)');
                setLoading(false);
                setMessages([]);
                setCurrentThreadId(null);
              }}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                backgroundColor: Colors.background.secondary,
                borderWidth: 1,
                borderColor: Colors.border.subtle,
                borderRadius: 999,
                paddingVertical: 8,
                paddingHorizontal: 12,
              }}
              accessibilityLabel="Start a new chat"
            >
              <Ionicons name="add-circle-outline" size={18} color={Colors.text.secondary} />
              <Text style={{ color: Colors.text.primary, fontSize: 13, fontWeight: '600' }}>
                New Chat
              </Text>
            </Pressable>
            <Pressable
              onPress={() => {
                console.log('🔘 [Button] History button clicked (empty state)');
                setHistoryOpen(true);
              }}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                backgroundColor: Colors.background.secondary,
                borderWidth: 1,
                borderColor: Colors.border.subtle,
                borderRadius: 999,
                paddingVertical: 8,
                paddingHorizontal: 12,
              }}
              accessibilityLabel="Open history"
            >
              <Ionicons name="time-outline" size={18} color={Colors.text.secondary} />
              <Text style={{ color: Colors.text.primary, fontSize: 13, fontWeight: '600' }}>
                History
              </Text>
            </Pressable>
          </View>
        </View>
      ) : (
        <>
          <View
            style={{
              position: 'absolute',
              right: 12,
              top: 12,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              zIndex: 2,
            }}
          >
            <Pressable
              onPress={() => {
                console.log('🆕 [Button] New chat clicked (active chat)');
                setLoading(false);
                setMessages([]);
                setCurrentThreadId(null);
              }}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                backgroundColor: Colors.background.secondary,
                borderWidth: 1,
                borderColor: Colors.border.subtle,
                borderRadius: 999,
                paddingVertical: 6,
                paddingHorizontal: 10,
              }}
              accessibilityLabel="Start a new chat"
            >
              <Ionicons name="add-circle-outline" size={16} color={Colors.text.secondary} />
              <Text style={{ color: Colors.text.primary, fontSize: 12, fontWeight: '600' }}>
                New
              </Text>
            </Pressable>
            <Pressable
              onPress={() => {
                console.log('🕘 [Button] History clicked (active chat)');
                setHistoryOpen(true);
              }}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                backgroundColor: Colors.background.secondary,
                borderWidth: 1,
                borderColor: Colors.border.subtle,
                borderRadius: 999,
                paddingVertical: 6,
                paddingHorizontal: 10,
              }}
              accessibilityLabel="Open history"
            >
              <Ionicons name="time-outline" size={16} color={Colors.text.secondary} />
              <Text style={{ color: Colors.text.primary, fontSize: 12, fontWeight: '600' }}>
                History
              </Text>
            </Pressable>
          </View>
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 90 }}>
            {messages.map(renderMessage)}
            {loading && (
              <View style={{ paddingHorizontal: 20, paddingVertical: 14 }}>
                <ActivityIndicator size="small" color={Colors.ai.primary} />
              </View>
            )}
          </ScrollView>
          <View style={{ position: 'absolute', left: 0, right: 0, bottom: 8 }}>
            <SearchBar
              placeholder="Ask anything about markets or stocks..."
              onSearch={handleSearch}
              onVoicePress={undefined}
            />
          </View>
        </>
      )}

      <Modal
        visible={historyOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setHistoryOpen(false)}
      >
        <Pressable
          style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.4)' }}
          onPress={() => setHistoryOpen(false)}
        />
        <View
          style={{
            position: 'absolute',
            left: 12,
            right: 12,
            top: 60,
            backgroundColor: Colors.background.secondary,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: Colors.background.tertiary,
            padding: 12,
            maxHeight: '70%',
          }}
        >
          <Text
            style={{ color: Colors.text.primary, fontWeight: '700', fontSize: 16, marginBottom: 8 }}
          >
            History
          </Text>

          <Text style={{ color: Colors.text.secondary, fontSize: 12, marginBottom: 6 }}>
            Conversations
          </Text>
          <ScrollView style={{ maxHeight: 220 }} contentContainerStyle={{ paddingVertical: 4 }}>
            {serverThreadsLoading ? (
              <View style={{ paddingVertical: 20, alignItems: 'center' }}>
                <ActivityIndicator size="small" color={Colors.accent.primary} />
                <Text style={{ color: Colors.text.tertiary, fontSize: 13, marginTop: 8 }}>
                  Loading history...
                </Text>
              </View>
            ) : threadLoadError ? (
              <View style={{ paddingVertical: 12 }}>
                <Text style={{ color: Colors.market.loss, fontSize: 13, marginBottom: 8 }}>
                  ⚠️ Error loading conversations
                </Text>
                <Text style={{ color: Colors.text.tertiary, fontSize: 11 }}>{threadLoadError}</Text>
                {mergedThreads.length > 0 && (
                  <Text style={{ color: Colors.text.secondary, fontSize: 11, marginTop: 8 }}>
                    Showing {mergedThreads.length} local conversation
                    {mergedThreads.length !== 1 ? 's' : ''}
                  </Text>
                )}
              </View>
            ) : mergedThreads.length === 0 ? (
              <Text style={{ color: Colors.text.tertiary, fontSize: 13 }}>
                No conversations yet
              </Text>
            ) : (
              mergedThreads.map(t => (
                <Pressable
                  key={t.id}
                  onPress={() => handleOpenThread(t.id)}
                  style={{
                    paddingVertical: 10,
                    paddingHorizontal: 8,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: Colors.background.tertiary,
                    marginBottom: 8,
                    backgroundColor: Colors.background.primary,
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <View style={{ flex: 1 }}>
                      <Text
                        numberOfLines={1}
                        style={{ color: Colors.text.primary, fontWeight: '600' }}
                      >
                        {t.title}
                      </Text>
                      <Text style={{ color: Colors.text.tertiary, fontSize: 11 }}>
                        {new Date(t.updatedAt).toLocaleString()}
                      </Text>
                    </View>
                    {t.source === 'server' && (
                      <View
                        style={{
                          marginLeft: 8,
                          paddingHorizontal: 6,
                          paddingVertical: 2,
                          borderRadius: 4,
                          backgroundColor: Colors.accent.primary + '20',
                        }}
                      >
                        <Text
                          style={{ color: Colors.accent.primary, fontSize: 9, fontWeight: '600' }}
                        >
                          CLOUD
                        </Text>
                      </View>
                    )}
                  </View>
                </Pressable>
              ))
            )}
          </ScrollView>

          <Text
            style={{ color: Colors.text.secondary, fontSize: 12, marginTop: 10, marginBottom: 6 }}
          >
            Tickers
          </Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ gap: 8, paddingBottom: 8 }}
          >
            {recentlyViewed.length === 0 ? (
              <Text style={{ color: Colors.text.tertiary, fontSize: 13 }}>No recent tickers</Text>
            ) : (
              recentlyViewed.map((rv: any) => (
                <Pressable
                  key={rv.ticker}
                  onPress={() =>
                    navigation?.navigate('StockAnalysis', {
                      ticker: rv.ticker,
                      companyName: rv.companyName,
                      sourceType: 'recent',
                      initialTab: 'chat',
                    })
                  }
                  style={{
                    paddingHorizontal: 10,
                    paddingVertical: 6,
                    borderRadius: 16,
                    backgroundColor: Colors.background.primary,
                    borderWidth: 1,
                    borderColor: Colors.border.subtle,
                  }}
                >
                  <Text style={{ color: Colors.text.primary, fontWeight: '700', fontSize: 12 }}>
                    {rv.ticker}
                  </Text>
                </Pressable>
              ))
            )}
          </ScrollView>

          <View style={{ alignItems: 'flex-end' }}>
            <Pressable
              onPress={() => {
                setLoading(false);
                setMessages([]);
                setCurrentThreadId(null);
              }}
              style={{ paddingVertical: 6, paddingHorizontal: 8 }}
            >
              <Text style={{ color: Colors.text.tertiary, fontSize: 12 }}>Clear current</Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      {/* Quick Stock Analysis Modal */}
      <Modal
        visible={stockOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setStockOpen(false)}
      >
        <Pressable
          style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.4)' }}
          onPress={() => setStockOpen(false)}
        />
        <View
          style={{
            position: 'absolute',
            left: 12,
            right: 12,
            top: 80,
            backgroundColor: Colors.background.secondary,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: Colors.background.tertiary,
            padding: 12,
          }}
        >
          <Text
            style={{
              color: Colors.text.primary,
              fontWeight: '700',
              fontSize: 16,
              marginBottom: 10,
            }}
          >
            Open Stock Analysis
          </Text>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              borderWidth: 1,
              borderColor: Colors.background.tertiary,
              borderRadius: 10,
              backgroundColor: Colors.background.primary,
              paddingHorizontal: 12,
              paddingVertical: 8,
            }}
          >
            <Ionicons name="search" size={18} color={Colors.text.tertiary} />
            <TextInput
              value={stockInput}
              onChangeText={t => setStockInput(t.toUpperCase())}
              placeholder="Ticker (e.g., NVDA)"
              placeholderTextColor={Colors.text.tertiary}
              autoCapitalize="characters"
              autoCorrect={false}
              onSubmitEditing={async () => {
                const sym = (stockInput || '').trim();
                if (!sym) return;
                setDetectError(null);
                setDetectSuggestions([]);
                setDetecting(true);
                try {
                  const det = await tickerDetectionService.detectTicker(sym);
                  if (det?.ticker && det.confidence >= 0.7) {
                    addRecentlyViewed(det.ticker, det.companyName || det.ticker);
                    setStockOpen(false);
                    setStockInput('');
                    navigation?.navigate('StockAnalysis', {
                      ticker: det.ticker,
                      companyName: det.companyName || det.ticker,
                      sourceType: 'home',
                      initialTab: 'outlook',
                    });
                  } else if (det?.suggestions?.length) {
                    setDetectError('Select a ticker from suggestions');
                    setDetectSuggestions(
                      det.suggestions.map((s: any) => ({ symbol: s.symbol, name: s.name }))
                    );
                  } else {
                    setDetectError('Enter a valid ticker symbol');
                  }
                } catch (e) {
                  setDetectError('Detection failed. Try again');
                } finally {
                  setDetecting(false);
                }
              }}
              style={{ flex: 1, color: Colors.text.primary, paddingLeft: 8, paddingVertical: 0 }}
              returnKeyType="go"
            />
            <Pressable
              disabled={detecting}
              onPress={async () => {
                const sym = (stockInput || '').trim();
                if (!sym) return;
                setDetectError(null);
                setDetectSuggestions([]);
                setDetecting(true);
                try {
                  const det = await tickerDetectionService.detectTicker(sym);
                  if (det?.ticker && det.confidence >= 0.7) {
                    addRecentlyViewed(det.ticker, det.companyName || det.ticker);
                    setStockOpen(false);
                    setStockInput('');
                    navigation?.navigate('StockAnalysis', {
                      ticker: det.ticker,
                      companyName: det.companyName || det.ticker,
                      sourceType: 'home',
                      initialTab: 'outlook',
                    });
                  } else if (det?.suggestions?.length) {
                    setDetectError('Select a ticker from suggestions');
                    setDetectSuggestions(
                      det.suggestions.map((s: any) => ({ symbol: s.symbol, name: s.name }))
                    );
                  } else {
                    setDetectError('Enter a valid ticker symbol');
                  }
                } catch (e) {
                  setDetectError('Detection failed. Try again');
                } finally {
                  setDetecting(false);
                }
              }}
              style={{
                marginLeft: 8,
                paddingHorizontal: 10,
                paddingVertical: 6,
                borderRadius: 8,
                backgroundColor: Colors.ai.primary,
                opacity: detecting ? 0.7 : 1,
              }}
            >
              <Text style={{ color: Colors.background.primary, fontWeight: '700' }}>Open</Text>
            </Pressable>
          </View>

          {detectError && (
            <Text style={{ color: Colors.status.warning, fontSize: 12, marginTop: 8 }}>
              {detectError}
            </Text>
          )}

          {detectSuggestions.length > 0 && (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ gap: 8, paddingVertical: 8 }}
            >
              {detectSuggestions.map(s => (
                <Pressable
                  key={s.symbol}
                  onPress={() => {
                    addRecentlyViewed(s.symbol, s.name || s.symbol);
                    setStockOpen(false);
                    setStockInput('');
                    setDetectSuggestions([]);
                    setDetectError(null);
                    navigation?.navigate('StockAnalysis', {
                      ticker: s.symbol,
                      companyName: s.name || s.symbol,
                      sourceType: 'home',
                      initialTab: 'outlook',
                    });
                  }}
                  style={{
                    paddingHorizontal: 10,
                    paddingVertical: 6,
                    borderRadius: 16,
                    backgroundColor: Colors.background.primary,
                    borderWidth: 1,
                    borderColor: Colors.border.subtle,
                  }}
                >
                  <Text style={{ color: Colors.text.primary, fontWeight: '700', fontSize: 12 }}>
                    {s.symbol}
                  </Text>
                </Pressable>
              ))}
            </ScrollView>
          )}

          {recentlyViewed.length > 0 && (
            <View style={{ marginTop: 12 }}>
              <Text style={{ color: Colors.text.secondary, fontSize: 12, marginBottom: 6 }}>
                Recent
              </Text>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ gap: 8 }}
              >
                {recentlyViewed.slice(0, 10).map((rv: any) => (
                  <Pressable
                    key={rv.ticker}
                    onPress={() => {
                      setStockOpen(false);
                      navigation?.navigate('StockAnalysis', {
                        ticker: rv.ticker,
                        companyName: rv.companyName,
                        sourceType: 'recent',
                        initialTab: 'outlook',
                      });
                    }}
                    style={{
                      paddingHorizontal: 10,
                      paddingVertical: 6,
                      borderRadius: 16,
                      backgroundColor: Colors.background.primary,
                      borderWidth: 1,
                      borderColor: Colors.border.subtle,
                    }}
                  >
                    <Text style={{ color: Colors.text.primary, fontWeight: '700', fontSize: 12 }}>
                      {rv.ticker}
                    </Text>
                  </Pressable>
                ))}
              </ScrollView>
            </View>
          )}
        </View>
      </Modal>
    </SafeAreaView>
  );
}

export const HomeScreen: React.FC<HomeScreenProps> = ({ navigation }) => {
  const isMinimalHome = Platform.OS === 'web';
  if (isMinimalHome) {
    return <MinimalHome navigation={navigation} />;
  }
  console.log('[HomeScreen] Component rendering...');
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const { logout, user } = useAuthStore();

  // Loading and error state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stock Chart state
  const [stockData, setStockData] = useState<StockDataResponse | null>(null);
  const [showChart, setShowChart] = useState(false);
  const [stockSymbol, setStockSymbol] = useState<string | null>(null);
  const [stockLoading, setStockLoading] = useState(false);

  // Current stock context for search bar
  const [currentStock, setCurrentStock] = useState<{
    symbol: string;
    name: string;
    hasHistory: boolean;
  } | null>(null);

  // Show stock history dropdown
  const [showStockHistory, setShowStockHistory] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<
    { y: number; height: number } | undefined
  >(undefined);

  // Context for AI continuation
  const [searchContext, setSearchContext] = useState<string>('');

  // Real-time market data from Polygon.io
  const {
    marketData,
    isLoading: marketLoading,
    error: marketError,
    isMarketOpen,
    marketStatus,
    refreshData,
    lastUpdated,
  } = useMarketData();

  // Watchlist management (for MarketCard star functionality)
  const { watchlist, addToWatchlist, removeFromWatchlist, isInWatchlist } = useWatchlist();

  // Recently viewed stocks
  const { recentlyViewed, addRecentlyViewed } = useRecentlyViewed();

  // Market movers (top gainers and losers)
  const { gainers, losers, isLoading: moversLoading } = useMarketMovers();
  const {
    events: macroEvents,
    isLoading: macroLoading,
    error: macroError,
    refresh: refreshMacro,
  } = useMacroForMe(12);
  // const { themes, isLoading: themesLoading, error: themesError, refresh: refreshThemes } = useThemesRadar(8);

  // Theme drill-in modal (HIDDEN - restore later if needed)
  // const [selectedThemeId, setSelectedThemeId] = useState<string | null>(null);
  // const { stories: themeStories, isLoading: themeStoriesLoading } = useThemeStories(selectedThemeId || undefined, 30);
  // Market news
  // Plaid link (dev-simple flow)
  const [linkModalVisible, setLinkModalVisible] = useState(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [linkTokenLoading, setLinkTokenLoading] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [publicToken, setPublicToken] = useState('');
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [holdingsSyncLoading, setHoldingsSyncLoading] = useState(false);
  const [holdingsSynced, setHoldingsSynced] = useState(false);

  const startPlaidLink = useCallback(async () => {
    try {
      setLinkError(null);
      setLinkTokenLoading(true);
      const { data, error } = await supabase.functions.invoke('plaid-link-start', {
        body: { user_id: user?.id || null },
      });
      if (error) throw error;
      setLinkToken(data?.link_token || null);
    } catch (e) {
      setLinkError(e instanceof Error ? e.message : 'Failed to create link token');
    } finally {
      setLinkTokenLoading(false);
    }
  }, [user?.id]);

  const exchangePublicToken = useCallback(async () => {
    try {
      setLinkError(null);
      if (!publicToken) {
        setLinkError('Enter the sandbox public_token from Plaid Link');
        return;
      }
      const { data, error } = await supabase.functions.invoke('plaid-link-callback', {
        body: { public_token: publicToken, user_id: user?.id || null },
      });
      if (error) throw error;
      const cid = data?.connection_id as string | undefined;
      if (!cid) {
        setLinkError('No connection_id returned');
        return;
      }
      setConnectionId(cid);
      setHoldingsSyncLoading(true);
      const { error: syncErr } = await supabase.functions.invoke('plaid-refresh-holdings', {
        body: { connection_id: cid },
      });
      if (syncErr) throw syncErr;
      setHoldingsSynced(true);
      setHasPortfolioConnection(true);
    } catch (e) {
      setLinkError(e instanceof Error ? e.message : 'Failed to link portfolio');
    } finally {
      setHoldingsSyncLoading(false);
    }
  }, [publicToken, user?.id]);
  const { news, isLoading: newsLoading, error: newsError, refreshNews } = useMarketNews();

  // Responsive layout
  const { isMobile, isTablet, isDesktop } = useResponsive();

  // Fetch index data separately (Polygon's stock snapshot doesn't support indexes)
  const [indexData, setIndexData] = useState<MarketData[]>([]);
  const [indexLoading, setIndexLoading] = useState(true);
  // Portfolio connection status
  const [hasPortfolioConnection, setHasPortfolioConnection] = useState<boolean>(false);
  const [portfolioCheckLoading, setPortfolioCheckLoading] = useState<boolean>(false);
  const [portfolioConnections, setPortfolioConnections] = useState<
    Array<{
      id: string;
      provider: string;
      status: string;
      last_sync_at: string | null;
      created_at: string;
    }>
  >([]);
  const [refreshingConnectionId, setRefreshingConnectionId] = useState<string | null>(null);
  const [positions, setPositions] = useState<any[]>([]);
  const [positionsLoading, setPositionsLoading] = useState(false);

  // Major event types for portfolio highlights
  const MAJOR_EVENT_TYPES = [
    'earnings',
    'fda',
    'product',
    'regulatory',
    'merger',
    'acquisition',
    'stock split',
    'dividend',
  ];

  useEffect(() => {
    const checkPortfolio = async () => {
      try {
        setPortfolioCheckLoading(true);
        if (!user?.id) {
          setHasPortfolioConnection(false);
          setPortfolioConnections([]);
          return;
        }

        const { data, error } = await getPortfolioConnections();

        if (error) {
          console.warn('[Home] portfolio_connections query failed:', error);
          setHasPortfolioConnection(false);
          setPortfolioConnections([]);
        } else if (data && data.length > 0) {
          setHasPortfolioConnection(true);
          setPortfolioConnections(data);
        } else {
          setHasPortfolioConnection(false);
          setPortfolioConnections([]);
        }
      } catch (e) {
        console.warn('[Home] portfolio_connections check error:', e);
        setHasPortfolioConnection(false);
        setPortfolioConnections([]);
      } finally {
        setPortfolioCheckLoading(false);
      }
    };
    checkPortfolio();
  }, [user?.id]);

  // Fetch portfolio positions
  const fetchPositions = async () => {
    if (!hasPortfolioConnection) {
      setPositions([]);
      return;
    }

    try {
      setPositionsLoading(true);
      console.log('[HomeScreen] Fetching portfolio positions...');

      const { data, error } = await getPortfolioPositions();

      if (error) {
        console.error('Error: [HomeScreen] Error fetching positions:', error);
        setPositions([]);
      } else if (data) {
        console.log(`Success: [HomeScreen] Fetched ${data.length} positions`);
        setPositions(data);
      } else {
        setPositions([]);
      }
    } catch (e) {
      console.error('Error: [HomeScreen] Unexpected error fetching positions:', e);
      setPositions([]);
    } finally {
      setPositionsLoading(false);
    }
  };

  // Fetch positions when portfolio connection changes
  useEffect(() => {
    if (hasPortfolioConnection) {
      fetchPositions();
    } else {
      setPositions([]);
    }
  }, [hasPortfolioConnection]);

  // Plaid handler functions
  const handlePlaidSuccess = async (connectionId: string) => {
    console.log('Success: [HomeScreen] Plaid link successful:', connectionId);

    // Fetch updated connections list to include the new connection
    const { data, error } = await getPortfolioConnections();
    if (data && data.length > 0) {
      setHasPortfolioConnection(true);
      setPortfolioConnections(data);
    }

    // Immediately refresh holdings after successful link
    handleRefreshHoldings(connectionId);
  };

  const handleRefreshHoldings = async (connectionId: string) => {
    if (!connectionId) {
      Alert.alert('Error', 'No portfolio connection found');
      return;
    }

    setRefreshingConnectionId(connectionId);

    try {
      const { data, error } = await refreshHoldings(connectionId);

      if (error) {
        Alert.alert('Refresh Failed', error);
        return;
      }

      Alert.alert(
        'Holdings Refreshed',
        `Successfully synced ${data?.positions_count || 0} positions from ${data?.accounts_count || 0} accounts.`
      );

      // Refetch connections to get updated last_sync_at times
      const { data: connectionsData } = await getPortfolioConnections();
      if (connectionsData && connectionsData.length > 0) {
        setPortfolioConnections(connectionsData);
      }

      // Refetch positions to show updated holdings
      await fetchPositions();
    } catch (err: any) {
      Alert.alert('Error', err.message || 'Failed to refresh holdings');
    } finally {
      setRefreshingConnectionId(null);
    }
  };

  const handleRefreshAllHoldings = async () => {
    if (portfolioConnections.length === 0) return;

    setRefreshingConnectionId('all');

    try {
      let totalPositions = 0;
      let totalAccounts = 0;

      // Refresh all connections sequentially
      for (const connection of portfolioConnections) {
        const { data, error } = await refreshHoldings(connection.id);
        if (!error && data) {
          totalPositions += data.positions_count || 0;
          totalAccounts += data.accounts_count || 0;
        }
      }

      Alert.alert(
        'All Holdings Refreshed',
        `Successfully synced ${totalPositions} positions from ${totalAccounts} accounts.`
      );

      // Refetch connections to get updated last_sync_at times
      const { data: connectionsData } = await getPortfolioConnections();
      if (connectionsData && connectionsData.length > 0) {
        setPortfolioConnections(connectionsData);
      }

      // Refetch positions to show updated holdings
      await fetchPositions();
    } catch (err: any) {
      Alert.alert('Error', err.message || 'Failed to refresh holdings');
    } finally {
      setRefreshingConnectionId(null);
    }
  };

  const handleDisconnectPortfolio = async (connectionId: string) => {
    if (!connectionId) return;

    Alert.alert(
      'Disconnect Portfolio',
      'Are you sure you want to disconnect this portfolio? Holdings from this account will no longer sync.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Disconnect',
          style: 'destructive',
          onPress: async () => {
            const { error } = await disconnectPortfolio(connectionId);

            if (error) {
              Alert.alert('Error', error);
              return;
            }

            // Refetch connections to update the list
            const { data: connectionsData } = await getPortfolioConnections();
            if (connectionsData && connectionsData.length > 0) {
              setPortfolioConnections(connectionsData);
              setHasPortfolioConnection(true);
            } else {
              setPortfolioConnections([]);
              setHasPortfolioConnection(false);
            }

            // Refetch positions to show updated holdings
            await fetchPositions();

            Alert.alert('Success', 'Portfolio disconnected successfully');
          },
        },
      ]
    );
  };

  // Portfolio events handlers
  const handleEventCardPress = (symbol: string, event: any) => {
    console.log('[HomeScreen] Event card pressed:', symbol, event.event_type);
    navigation?.navigate('StockAnalysis', {
      ticker: symbol,
      companyName: symbol, // Will be resolved in StockAnalysis screen
      sourceType: 'portfolio_event',
    });
  };

  const handleEventArticlePress = async (symbol: string, event: any) => {
    console.log('[HomeScreen] Finding top article for:', symbol, event.event_type);

    try {
      // Query news_item table for articles related to this symbol
      const { data, error } = await (supabase as any)
        .from('news_item')
        .select('url, title, published_at')
        .contains('symbols', [symbol])
        .gte('published_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()) // Last 7 days
        .order('published_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error: [HomeScreen] Error fetching article:', error);
        Alert.alert('Error', 'Could not find related article');
        return;
      }

      const article: any = data as any;
      if (!article || !article.url) {
        Alert.alert('No Article Found', `No recent articles found for ${symbol}`);
        return;
      }

      console.log('Success: [HomeScreen] Opening article:', article.title);
      await Linking.openURL(article.url);
    } catch (err: any) {
      console.error('Error: [HomeScreen] Unexpected error:', err);
      Alert.alert('Error', err.message || 'Failed to open article');
    }
  };

  // Handle position click - navigate to stock analysis
  const handlePositionPress = async (ticker?: string, name?: string | null) => {
    if (!ticker) {
      console.warn('[HomeScreen] Position clicked without ticker');
      return;
    }

    const companyName = name || ticker;

    // Track as recently viewed
    await addRecentlyViewed(ticker, companyName);

    // Navigate to stock analysis
    navigation?.navigate('StockAnalysis', {
      ticker,
      companyName,
      sourceType: 'portfolio',
      initialTab: 'outlook',
    });
  };

  useEffect(() => {
    const fetchIndexes = async () => {
      console.log('[HomeScreen] Starting index fetch using Polygon...');
      setIndexLoading(true);

      const { data, error } = await polygonMarketDataService.getIndices();

      if (error) {
        console.error('Error: [HomeScreen] Failed to fetch indices:', error);
        setIndexLoading(false);
        return;
      }

      if (data) {
        // Transform to MarketData format
        const transformedData: MarketData[] = data.map(quote => ({
          symbol: quote.ticker,
          name: quote.name || quote.ticker,
          price: quote.price,
          change: quote.change,
          changePercent: quote.changePercent,
        }));

        console.log(
          `Success: [HomeScreen] Loaded ${transformedData.length} indices:`,
          transformedData
        );
        setIndexData(transformedData);
      }

      setIndexLoading(false);
    };

    fetchIndexes();
  }, []);

  // Separate stocks from market data (excluding indexes which are fetched separately)
  const stocks = marketData;

  // Map Polygon index symbols to Yahoo symbols for sparkline charts
  const polygonToYahooMap: { [key: string]: string } = {
    'I:SPX': '^GSPC',
    'I:COMP': '^IXIC',
    'I:DJI': '^DJI',
  };

  // Fetch sparkline chart data for indexes using Yahoo symbols
  const yahooIndexSymbols = ['^GSPC', '^IXIC', '^DJI'];
  const { chartDataMap: rawIndexChartData, isLoading: indexChartsLoading } =
    useStockSparklines(yahooIndexSymbols);

  // Remap chart data to use Polygon symbols as keys
  const indexChartData: { [key: string]: number[] } = {};
  indexData.forEach(index => {
    const yahooSymbol = polygonToYahooMap[index.symbol];
    if (yahooSymbol && rawIndexChartData[yahooSymbol]) {
      indexChartData[index.symbol] = rawIndexChartData[yahooSymbol];
    }
  });

  // Generate dynamic suggestions based on recently viewed
  const generateSmartSuggestions = useCallback(() => {
    const suggestions: string[] = [];

    // Personalized suggestions based on recently viewed
    if (recentlyViewed.length > 0) {
      const topRecent = recentlyViewed.slice(0, 2);
      topRecent.forEach(item => {
        suggestions.push(`What's driving ${item.ticker} today?`);
      });
    }

    // Generic market suggestions as fallback
    const genericSuggestions = [
      'Why is tech sector up?',
      'Best AI stocks to watch',
      'Market sentiment analysis',
      'Top gainers today',
      'Semiconductor industry outlook',
      'Fed rate decision impact',
    ];

    // Fill remaining slots with generic suggestions
    while (suggestions.length < 4) {
      const randomIndex = Math.floor(Math.random() * genericSuggestions.length);
      const suggestion = genericSuggestions[randomIndex];
      if (!suggestions.includes(suggestion)) {
        suggestions.push(suggestion);
      }
    }

    return suggestions;
  }, [recentlyViewed]);

  const [suggestions, setSuggestions] = useState<string[]>(generateSmartSuggestions());

  // Update suggestions when watchlist or recently viewed changes
  useEffect(() => {
    setSuggestions(generateSmartSuggestions());
  }, [generateSmartSuggestions]);

  useEffect(() => {
    // Fade in animation on mount
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 500,
      useNativeDriver: true,
    }).start();
  }, [fadeAnim]);

  // Clear state when returning to HomeScreen (but not on initial mount)
  const isFirstRender = useRef(true);

  useFocusEffect(
    useCallback(() => {
      // Skip on first render (when component mounts)
      if (isFirstRender.current) {
        isFirstRender.current = false;
        console.log('HomeScreen mounted - skipping state clear');
        return;
      }

      // Clear conversation state when returning from another screen
      setCurrentStock(null);
      setSearchContext('');
      setShowChart(false);
      setStockData(null);
      setStockSymbol(null);
      setError(null);

      console.log('HomeScreen focused - state cleared (returning from another screen)');
    }, [])
  );

  /**
   * Handle stock selection from history dropdown
   * Navigate to StockAnalysisScreen with recent searches context
   */
  const handleStockSelect = async (summary: StockSearchSummary) => {
    console.log('Stock selected from history:', summary.stock_symbol);

    try {
      // Track as recently viewed
      await addRecentlyViewed(summary.stock_symbol, summary.stock_name);

      // Navigate to StockAnalysis with recent searches
      navigation?.navigate('StockAnalysis', {
        ticker: summary.stock_symbol,
        companyName: summary.stock_name,
        sourceType: 'recent',
        initialTab: 'outlook',
      });

      console.log('Success: Navigated to stock analysis for', summary.stock_symbol);
    } catch (error) {
      console.error('Error navigating to stock analysis:', error);
    }
  };

  /**
   * Main search handler with database persistence and chat navigation
   */
  const handleSearch = async (query: string) => {
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setShowChart(false);
    setStockData(null);

    try {
      // STEP 1: Detect ticker using intelligent detection service
      console.log('Starting ticker detection for query:', query);
      const detection = await tickerDetectionService.detectTicker(query);
      console.log('Detection result:', {
        ticker: detection.ticker,
        confidence: detection.confidence,
        source: detection.source,
        companyName: detection.companyName,
        suggestionsCount: detection.suggestions.length,
      });

      // STEP 2: If ticker detected with good confidence, navigate to stock analysis
      if (detection.ticker && detection.confidence >= 0.7) {
        console.log(
          'Stock detected with good confidence:',
          detection.ticker,
          'confidence:',
          detection.confidence
        );

        // Check navigation object availability
        if (!navigation) {
          console.error('Error: Navigation object is undefined! Cannot navigate to StockAnalysis');
          setError('Navigation error - please try again');
          setLoading(false);
          return;
        }

        // Track as recently viewed
        await addRecentlyViewed(detection.ticker, detection.companyName || detection.ticker);

        console.log('Navigating to StockAnalysis with Chat tab');
        // Navigate to stock analysis screen with chat tab and user's query
        navigation.navigate('StockAnalysis', {
          ticker: detection.ticker,
          companyName: detection.companyName,
          sourceType: 'recent',
          initialTab: 'chat',
          initialQuery: query,
        });

        setLoading(false);
        return;
      }

      // STEP 3: If multiple suggestions (ambiguous) or no ticker detected
      if ((detection.suggestions.length > 1 && !detection.ticker) || !detection.ticker) {
        console.log('Question: No specific ticker detected - please search for a stock symbol');
        setError('Please search for a specific stock symbol (e.g., AAPL, TSLA, NVDA)');
        setLoading(false);
        return;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      console.error('Unexpected search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionPress = (suggestion: string) => {
    handleSearch(suggestion);
  };

  const handleMarketCardPress = async (symbol: string) => {
    // Find the market data for this symbol
    const marketItem = marketData.find(item => item.symbol === symbol);

    if (marketItem) {
      // Track as recently viewed
      addRecentlyViewed(marketItem.symbol, marketItem.name);

      // Navigate to stock analysis
      navigation?.navigate('StockAnalysis', {
        ticker: marketItem.symbol,
        companyName: marketItem.name,
        sourceType: 'recent',
        initialTab: 'outlook',
      });
    }
  };

  const handleWatchlistToggle = async (ticker: string, companyName: string) => {
    if (isInWatchlist(ticker)) {
      await removeFromWatchlist(ticker);
    } else {
      await addToWatchlist(ticker, companyName);
    }
  };

  const handleViewAllWatchlist = () => {
    navigation?.navigate('Watchlist');
  };

  const handleVoicePress = () => {
    console.log('Voice search pressed');
    // TODO: Implement voice search
  };

  const handleRetry = () => {
    // Retry functionality removed - use clock dropdown for recent searches
  };

  // Filter major events for highlights - one per symbol (includes watchlist + portfolio)
  const portfolioEventHighlights = React.useMemo(() => {
    if (macroEvents.length === 0) return [];

    const majorEvents = macroEvents.filter(event => {
      const eventTypeLower = (event.event_type || '').toLowerCase();
      return MAJOR_EVENT_TYPES.some(majorType => eventTypeLower.includes(majorType));
    });

    // Get earliest event for each symbol
    const eventsBySymbol = new Map<string, any>();
    majorEvents.forEach(event => {
      if (!eventsBySymbol.has(event.symbol)) {
        eventsBySymbol.set(event.symbol, {
          id: `${event.symbol}-${event.event_type}-${event.when_utc}`,
          symbol: event.symbol,
          event_type: event.event_type || 'Event',
          event_date: event.when_utc,
          description: event.notes || 'No description available',
          impact: 'neutral', // Default impact
          created_at: new Date().toISOString(),
        });
      }
    });

    return Array.from(eventsBySymbol.values());
  }, [macroEvents, MAJOR_EVENT_TYPES]);

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" />
      <Animated.View style={[styles.container, { opacity: fadeAnim }]}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={marketLoading}
              onRefresh={refreshData}
              tintColor={Colors.ai.primary}
              colors={[Colors.ai.primary]}
            />
          }
        >
          {/* TOP SECTION - Logo/Name */}
          <View style={styles.topSection}>
            <View style={styles.topRow}>
              <View>
                <Text style={styles.appName}>TradePlatform</Text>
                <Text style={styles.tagline}>AI-Powered Market Insights</Text>
              </View>
            </View>

            {/* Logout Button */}
            <Pressable onPress={logout} style={styles.logoutButton}>
              <Ionicons name="log-out-outline" size={20} color={Colors.text.secondary} />
              <Text style={styles.logoutText}>Logout</Text>
            </Pressable>
          </View>

          {/* SEARCH SECTION */}
          <SearchBar
            onSearch={handleSearch}
            onVoicePress={handleVoicePress}
            currentStock={currentStock}
            onTickerPress={layout => {
              // Save position and show stock history dropdown
              setDropdownPosition(layout);
              setShowStockHistory(true);
            }}
          />

          {/* Context Indicator */}
          {searchContext && (
            <View style={styles.contextBadge}>
              <Ionicons
                name="chatbubbles-outline"
                size={14}
                color={Colors.ai.primary}
                style={styles.contextIcon}
              />
              <Text style={styles.contextText}>Continuing conversation about {stockSymbol}</Text>
              <Pressable onPress={() => setSearchContext('')} style={styles.clearContext}>
                <Ionicons name="close-circle" size={16} color={Colors.text.tertiary} />
              </Pressable>
            </View>
          )}

          {/* SUGGESTED QUESTIONS */}
          <View style={styles.suggestionsSection}>
            <Text style={styles.suggestionsLabel}>Try:</Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.suggestionsContainer}
            >
              {suggestions.map(suggestion => (
                <Pressable
                  key={suggestion}
                  onPress={() => handleSuggestionPress(suggestion)}
                  style={styles.suggestionChip}
                >
                  <Text style={styles.suggestionText}>{suggestion}</Text>
                </Pressable>
              ))}
            </ScrollView>
          </View>

          {/* STOCK CHART SECTION */}
          {showChart && stockData && stockData.quote && !stockData.error && (
            <View style={styles.chartContainer}>
              <StockChart
                symbol={stockSymbol || ''}
                data={stockData.chartData}
                quote={stockData.quote}
                loading={stockLoading}
              />
            </View>
          )}

          {/* STOCK DATA ERROR */}
          {stockData && stockData.error && (
            <View style={styles.stockErrorContainer}>
              <Ionicons
                name="information-circle-outline"
                size={24}
                color={Colors.status.warning}
                style={styles.inlineIcon}
              />
              <Text style={styles.stockErrorText}>{stockData.error}</Text>
            </View>
          )}

          {/* AI INSIGHTS RESULTS SECTION */}
          {loading && (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={Colors.ai.primary} />
              <Text style={styles.loadingText}>
                {stockLoading ? 'Loading stock data...' : 'Getting AI insights...'}
              </Text>
            </View>
          )}

          {error && !loading && (
            <View style={styles.errorContainer}>
              <Ionicons name="alert-circle-outline" size={48} color={Colors.status.error} />
              <Text style={styles.errorTitle}>Something went wrong</Text>
              <Text style={styles.errorMessage}>{error}</Text>
            </View>
          )}

          {/* TODAY'S MARKET BRIEF */}
          <CollapsibleSection title="Today's Market Brief" defaultExpanded={true}>
            <NewsSnippets
              news={news}
              isLoading={newsLoading}
              error={newsError}
              onRefresh={refreshNews}
            />
          </CollapsibleSection>

          {/* Theme drill-in modal - HIDDEN (restore later if needed) */}
          {/* <Modal
            visible={!!selectedThemeId}
            transparent
            animationType="slide"
            onRequestClose={() => setSelectedThemeId(null)}
          >
            <View style={styles.modalBackdrop}>
              <View style={styles.modalCard}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Theme Stories</Text>
                  <Pressable onPress={() => setSelectedThemeId(null)}>
                    <Ionicons name="close" size={22} color={Colors.text.secondary} />
                  </Pressable>
                </View>
                {themeStoriesLoading ? (
                  <View style={styles.marketLoadingContainer}>
                    <ActivityIndicator size="small" color={Colors.ai.primary} />
                    <Text style={styles.marketLoadingText}>Loading stories...</Text>
                  </View>
                ) : themeStories.length === 0 ? (
                  <View style={styles.marketLoadingContainer}>
                    <Ionicons name="newspaper-outline" size={24} color={Colors.text.tertiary} />
                    <Text style={styles.marketLoadingText}>No matching stories</Text>
                  </View>
                ) : (
                  <ScrollView style={{ maxHeight: 380 }} contentContainerStyle={{ paddingBottom: 12 }}>
                    {themeStories.map(s => (
                      <Pressable key={s.id} style={styles.storyRow} onPress={() => { s.url && Linking.openURL(s.url).catch(() => {}); }}>
                        <Text style={styles.storyTitle} numberOfLines={2}>{s.title}</Text>
                        <Text style={styles.storyMeta}>{new Date(s.published_at).toLocaleString()} - {s.source || ''}</Text>
                      </Pressable>
                    ))}
                  </ScrollView>
                )}
              </View>
            </View>
          </Modal> */}
          {/* Plaid link (dev simple) */}
          <Modal
            visible={linkModalVisible}
            transparent
            animationType="slide"
            onRequestClose={() => setLinkModalVisible(false)}
          >
            <View style={styles.modalBackdrop}>
              <View style={styles.modalCard}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Connect Portfolio (Plaid Sandbox)</Text>
                  <Pressable onPress={() => setLinkModalVisible(false)}>
                    <Ionicons name="close" size={22} color={Colors.text.secondary} />
                  </Pressable>
                </View>
                {linkError ? (
                  <View style={styles.marketErrorContainer}>
                    <Ionicons
                      name="alert-circle-outline"
                      size={20}
                      color={Colors.status.error}
                      style={styles.inlineIcon}
                    />
                    <Text style={styles.marketLoadingText}>{linkError}</Text>
                  </View>
                ) : null}
                {!linkToken ? (
                  <View style={styles.marketLoadingContainer}>
                    {linkTokenLoading ? (
                      <ActivityIndicator size="small" color={Colors.ai.primary} />
                    ) : (
                      <Button title="Get Link Token" onPress={startPlaidLink} />
                    )}
                  </View>
                ) : (
                  <View style={{ gap: 10 }}>
                    <Text style={styles.marketLoadingText}>Link token (sandbox):</Text>
                    <View style={styles.tokenBox}>
                      <Text style={styles.tokenText} selectable>
                        {linkToken}
                      </Text>
                    </View>
                    <Text style={styles.marketLoadingText}>
                      After completing Plaid Link (sandbox), paste the public_token here:
                    </Text>
                    <TextInput
                      style={styles.input}
                      placeholder="public-sandbox-..."
                      placeholderTextColor={Colors.text.tertiary}
                      value={publicToken}
                      onChangeText={setPublicToken}
                      autoCapitalize="none"
                      autoCorrect={false}
                    />
                    <View style={{ marginTop: 8 }}>
                      {holdingsSyncLoading ? (
                        <ActivityIndicator size="small" color={Colors.ai.primary} />
                      ) : (
                        <Button title="Exchange & Sync Holdings" onPress={exchangePublicToken} />
                      )}
                    </View>
                    {holdingsSynced && (
                      <Text style={styles.marketLoadingText}>
                        Linked and synced! You can close this modal.
                      </Text>
                    )}
                  </View>
                )}
              </View>
            </View>
          </Modal>

          {/* THEMES RADAR - HIDDEN (restore later if needed) */}
          {/* <CollapsibleSection title="Themes Radar" defaultExpanded={true}>
            {themesLoading ? (
              <View style={styles.marketLoadingContainer}>
                <ActivityIndicator size="small" color={Colors.ai.primary} />
                <Text style={styles.marketLoadingText}>Analyzing themes...</Text>
              </View>
            ) : themesError ? (
              <View style={styles.marketErrorContainer}>
                <Ionicons name="alert-circle-outline" size={20} color={Colors.status.warning} style={styles.inlineIcon} />
                <Text style={styles.marketErrorText}>{themesError}</Text>
              </View>
            ) : themes.length === 0 ? (
              <View style={styles.marketLoadingContainer}>
                <Ionicons name="pricetags-outline" size={24} color={Colors.text.tertiary} />
                <Text style={styles.marketLoadingText}>No active themes detected</Text>
              </View>
            ) : (
              <View style={{ gap: 10, marginHorizontal: 24 }}>
                {themes.map((t) => (
                  <Pressable key={t.theme_id} style={styles.themeCard} onPress={() => setSelectedThemeId(t.theme_id)}>
                    <View style={styles.themeHeader}>
                      <Text style={styles.themeTitle}>{t.name}</Text>
                      <Text style={styles.themeMeta}>Strength {t.strength} - Momentum {(t.momentum||0).toFixed(2)}</Text>
                    </View>
                    {t.top_symbols?.length ? (
                      <View style={styles.briefChips}>
                        {t.top_symbols.slice(0,6).map(sym => (
                          <View key={sym} style={styles.chip}><Text style={styles.chipText}>{sym}</Text></View>
                        ))}
                      </View>
                    ) : null}
                    {t.sample_story ? (
                      <Text style={styles.themeStory} numberOfLines={2}>{t.sample_story}</Text>
                    ) : null}
                  </Pressable>
                ))}
              </View>
            )}
          </CollapsibleSection> */}

          {/* MACRO STRIP (upcoming events for my symbols) */}
          <CollapsibleSection title="Upcoming for My Symbols" defaultExpanded={true}>
            {macroLoading ? (
              <View style={styles.marketLoadingContainer}>
                <ActivityIndicator size="small" color={Colors.ai.primary} />
                <Text style={styles.marketLoadingText}>Loading upcoming events...</Text>
              </View>
            ) : macroError ? (
              <View style={styles.marketErrorContainer}>
                <Ionicons
                  name="alert-circle-outline"
                  size={20}
                  color={Colors.status.warning}
                  style={styles.inlineIcon}
                />
                <Text style={styles.marketErrorText}>{macroError}</Text>
              </View>
            ) : macroEvents.length === 0 ? (
              <View style={styles.marketLoadingContainer}>
                <Ionicons name="calendar-outline" size={24} color={Colors.text.tertiary} />
                <Text style={styles.marketLoadingText}>No upcoming events in the next 2 weeks</Text>
              </View>
            ) : (
              <View style={{ gap: 10, marginHorizontal: 24 }}>
                {macroEvents.map((ev, idx) => (
                  <View key={`${ev.symbol}-${idx}`} style={styles.macroRow}>
                    <View style={styles.macroLeft}>
                      <Text style={styles.macroSymbol}>{ev.symbol}</Text>
                      <Text style={styles.macroType}>{ev.event_type || 'event'}</Text>
                    </View>
                    <View style={styles.macroCenter}>
                      <Text style={styles.macroNotes} numberOfLines={2}>
                        {ev.notes || ''}
                      </Text>
                    </View>
                    <View style={styles.macroRight}>
                      <Text style={styles.macroWhen}>{new Date(ev.when_utc).toLocaleString()}</Text>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </CollapsibleSection>

          {/* MAJOR EVENTS HIGHLIGHTS - Major events for watchlist + portfolio holdings */}
          {portfolioEventHighlights.length > 0 && (
            <CollapsibleSection title="Major Events" defaultExpanded={true}>
              <UpcomingEventsSnippets
                events={portfolioEventHighlights}
                onCardPress={handleEventCardPress}
                onArticlePress={handleEventArticlePress}
              />
            </CollapsibleSection>
          )}

          {/* MY EXPOSURE SECTION - P1 Plaid Integration */}
          <CollapsibleSection title="My Exposure" defaultExpanded={true}>
            {portfolioCheckLoading ? (
              <View style={styles.marketLoadingContainer}>
                <ActivityIndicator size="small" color={Colors.ai.primary} />
                <Text style={styles.marketLoadingText}>Checking portfolio status...</Text>
              </View>
            ) : hasPortfolioConnection ? (
              <View style={styles.exposureContainer}>
                <View style={styles.exposureConnectedHeader}>
                  <View style={styles.exposureHeaderLeft}>
                    <Ionicons name="checkmark-circle" size={20} color={Colors.status.success} />
                    <Text style={styles.exposureConnectedText}>
                      {portfolioConnections.length === 1
                        ? 'Portfolio Connected'
                        : `${portfolioConnections.length} Portfolios Connected`}
                    </Text>
                  </View>
                  <Pressable
                    onPress={handleRefreshAllHoldings}
                    disabled={refreshingConnectionId === 'all'}
                    style={styles.refreshIconButton}
                  >
                    {refreshingConnectionId === 'all' ? (
                      <ActivityIndicator size="small" color={Colors.ai.primary} />
                    ) : (
                      <Ionicons name="refresh" size={20} color={Colors.ai.primary} />
                    )}
                  </Pressable>
                </View>

                {/* Compact connection info */}
                {portfolioConnections.length > 0 && portfolioConnections[0].last_sync_at && (
                  <Text style={styles.exposureLastSyncCompact}>
                    Last synced:{' '}
                    {new Date(portfolioConnections[0].last_sync_at).toLocaleTimeString()}
                  </Text>
                )}

                {/* Holdings Display */}
                {positionsLoading ? (
                  <View style={styles.holdingsLoadingContainer}>
                    <ActivityIndicator size="small" color={Colors.ai.primary} />
                    <Text style={styles.holdingsLoadingText}>Loading holdings...</Text>
                  </View>
                ) : positions.length > 0 ? (
                  <View style={styles.holdingsContainer}>
                    <Text style={styles.holdingsTitle}>
                      Your Holdings (Combined from all accounts):
                    </Text>
                    {positions.map((position, index) => (
                      <Pressable
                        key={position.id || index}
                        style={({ pressed }) => [
                          styles.positionRow,
                          pressed && styles.positionRowPressed,
                        ]}
                        onPress={() =>
                          handlePositionPress(
                            position.instrument?.symbol,
                            position.instrument?.name
                          )
                        }
                      >
                        <View style={styles.positionMain}>
                          <Text style={styles.positionSymbol}>
                            {position.instrument?.symbol || 'N/A'}
                          </Text>
                          <Text style={styles.positionName}>
                            {position.instrument?.name || 'Unknown'}
                          </Text>
                        </View>
                        <View style={styles.positionDetails}>
                          <Text style={styles.positionQuantity}>{position.quantity} shares</Text>
                          {position.avg_price && (
                            <Text style={styles.positionPrice}>
                              @ ${position.avg_price.toFixed(2)}
                            </Text>
                          )}
                        </View>
                      </Pressable>
                    ))}
                  </View>
                ) : (
                  <View style={styles.holdingsEmptyContainer}>
                    <Text style={styles.holdingsEmptyText}>
                      No holdings found. Your portfolio may be empty or holdings haven't synced yet.
                    </Text>
                  </View>
                )}

                {/* Add Another Connection - Compact */}
                <Pressable
                  onPress={() => {}} // PlaidLinkButton will handle the actual press
                  style={styles.addConnectionContainer}
                >
                  <PlaidLinkButton
                    onSuccess={handlePlaidSuccess}
                    onError={error => console.error('Plaid error:', error)}
                    buttonText="+ Add Another Account"
                    style={styles.addConnectionButton}
                  />
                </Pressable>
              </View>
            ) : (
              <View style={styles.exposureCtaContainer}>
                <Text style={styles.exposureTitle}>Connect Your Portfolio</Text>
                <Text style={styles.exposureSubtitle}>
                  Link your brokerage (read-only via Plaid) to personalize Today's Brief and
                  exposure.
                </Text>
                <PlaidLinkButton
                  onSuccess={handlePlaidSuccess}
                  onError={error => console.error('Plaid error:', error)}
                  buttonText="Connect Portfolio (Read-only)"
                />
              </View>
            )}
          </CollapsibleSection>

          {/* MARKET OVERVIEW SECTION - HIDDEN (restore later if needed) */}
          {/* <CollapsibleSection title="Market Overview" defaultExpanded={true}>
            <View style={styles.marketHeader}>
              <MarketStatusBadge status={marketStatus} lastUpdated={lastUpdated} />
            </View>

            {marketError && (
              <View style={styles.marketErrorContainer}>
                <Ionicons name="alert-circle-outline" size={20} color={Colors.status.warning} style={styles.inlineIcon} />
                <Text style={styles.marketErrorText}>{marketError} - Showing cached data</Text>
              </View>
            )}

            {/* Major Indexes */}
          {/* <View style={[styles.indexColumn, isMobile && styles.columnMobile]}>
              <Text style={styles.columnTitle}>Major Indexes</Text>
                {indexLoading && indexData.length === 0 ? (
                  <View style={styles.marketLoadingContainer}>
                    <ActivityIndicator size="small" color={Colors.ai.primary} />
                    <Text style={styles.marketLoadingText}>Loading indexes...</Text>
                  </View>
                ) : indexData.length === 0 ? (
                  <View style={styles.marketLoadingContainer}>
                    <Ionicons name="bar-chart-outline" size={32} color={Colors.text.tertiary} />
                    <Text style={styles.marketLoadingText}>No index data available</Text>
                  </View>
                ) : (
                  <View style={styles.indexList}>
                    {indexData.map(index => {
                      const isPositive = index.changePercent >= 0;
                      const changeColor = isPositive ? Colors.market.profit : Colors.market.loss;

                      return (
                        <Pressable
                          key={index.symbol}
                          style={styles.indexItem}
                          onPress={() => handleMarketCardPress(index.symbol)}
                        >
                          {/* Left: Index name and symbol */}
          {/* <View style={styles.indexInfo}>
                            <Text style={styles.indexName}>{index.name}</Text>
                            <Text style={styles.indexSymbol}>{index.symbol}</Text>
                          </View>

                          {/* Middle: Sparkline chart */}
          {/* <View style={styles.indexChartSection}>
                            {indexChartData[index.symbol] ? (
                              <MiniSparkline
                                data={indexChartData[index.symbol]}
                                width={60}
                                height={30}
                              />
                            ) : (
                              <ActivityIndicator size="small" color={Colors.text.tertiary} />
                            )}
                          </View>

                          {/* Right: Price and change */}
          {/* <View style={styles.indexPriceInfo}>
                            <Text style={styles.indexPrice}>
                              {index.price.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </Text>
                            <Text style={[styles.indexChange, { color: changeColor }]}>
                              {isPositive ? '+' : ''}
                              {index.changePercent.toFixed(2)}%
                            </Text>
                          </View>
                        </Pressable>
                      );
                    })}
                  </View>
                )}
              </View>
          </CollapsibleSection> */}

          {/* MARKET MOVERS SECTION - Swipable Tabs */}
          <SwipableMarketMovers
            gainers={gainers}
            losers={losers}
            onMoverPress={handleMarketCardPress}
            isLoading={moversLoading}
          />

          {/* BOTTOM PADDING */}
          <View style={styles.bottomPadding} />
        </ScrollView>
      </Animated.View>
      {showStockHistory && (
        <StockHistoryDropdown
          visible={showStockHistory}
          position={dropdownPosition}
          onStockSelect={summary => {
            setShowStockHistory(false);
            handleStockSelect(summary);
          }}
          onClose={() => setShowStockHistory(false)}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  topSection: {
    paddingTop: Spacing.xl,
    paddingHorizontal: Spacing.xl,
    paddingBottom: Spacing.lg,
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: Spacing.lg,
  },
  appName: {
    ...Typography.headline,
    fontSize: 28,
    color: Colors.text.primary,
  },
  tagline: {
    ...Typography.bodySmall,
    color: Colors.text.secondary,
    marginTop: Spacing.xs,
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: Spacing.xs,
    paddingHorizontal: Spacing.sm,
  },
  logoutText: {
    ...Typography.bodySmall,
    color: Colors.text.secondary,
    marginLeft: Spacing.xs,
  },
  contextBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.surface.base,
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.md,
    marginHorizontal: Spacing.xl,
    marginTop: Spacing.sm,
    marginBottom: Spacing.sm,
    borderRadius: Radius.md,
    borderWidth: 1,
    borderColor: Colors.ai.primary,
    ...Elevation.level1,
  },
  contextIcon: {
    marginRight: Spacing.sm,
  },
  contextText: {
    flex: 1,
    ...Typography.bodySmall,
    color: Colors.text.primary,
  },
  clearContext: {
    padding: Spacing.xs,
    marginLeft: Spacing.sm,
  },
  suggestionsSection: {
    marginTop: Spacing.xl,
    marginHorizontal: Spacing.xl,
    marginBottom: Spacing.xl,
  },
  suggestionsLabel: {
    ...Typography.caption,
    color: Colors.text.secondary,
    marginBottom: Spacing.sm,
  },
  suggestionsContainer: {
    paddingRight: Spacing.xl,
  },
  suggestionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.surface.raised,
    borderWidth: 1,
    borderColor: Colors.accent.primary,
    ...Elevation.level1,
    paddingVertical: Spacing.xs,
    paddingHorizontal: Spacing.md,
    borderRadius: Radius.pill,
    marginRight: Spacing.sm,
  },
  suggestionText: {
    ...Typography.bodySmall,
    color: Colors.accent.secondary,
  },
  chartContainer: {
    marginHorizontal: Spacing.xl,
    marginBottom: Spacing.xl,
  },
  stockErrorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: Spacing.xl,
    marginBottom: Spacing.xl,
    padding: Spacing.md,
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    borderRadius: Radius.md,
    borderLeftWidth: 3,
    borderLeftColor: Colors.status.warning,
  },
  inlineIcon: {
    marginRight: Spacing.sm,
  },
  stockErrorText: {
    flex: 1,
    ...Typography.bodySmall,
    color: Colors.text.secondary,
  },
  watchlistSection: {
    marginBottom: 32,
  },
  watchlistHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 24,
    marginBottom: 16,
    gap: 8,
  },
  recentlyViewedSection: {
    marginBottom: 32,
  },
  recentlyViewedHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 24,
    marginBottom: 16,
    gap: 8,
  },
  marketSection: {
    marginBottom: 24,
  },
  marketHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginHorizontal: 24,
    marginBottom: 16,
  },
  sectionHeader: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  marketErrorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: Spacing.xl,
    marginBottom: Spacing.md,
    padding: Spacing.md,
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    borderRadius: Radius.md,
  },
  marketErrorText: {
    flex: 1,
    fontSize: 12,
    color: Colors.text.secondary,
  },
  marketLoadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    marginHorizontal: 24,
    gap: 12,
  },
  marketLoadingText: {
    fontSize: 14,
    color: Colors.text.secondary,
  },
  marketOverviewGrid: {
    flexDirection: 'row',
    marginHorizontal: 24,
    gap: 16,
    marginTop: 8,
  },
  marketOverviewGridMobile: {
    flexDirection: 'column',
    marginHorizontal: 16,
    gap: 24,
  },
  newsColumn: {
    flex: 1,
    minWidth: 0,
  },
  indexColumn: {
    flex: 1,
    minWidth: 0,
  },
  columnMobile: {
    width: '100%',
  },
  columnTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.secondary,
    marginBottom: 12,
  },
  indexList: {
    gap: 12,
  },
  indexItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: Colors.background.secondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    gap: 12,
  },
  indexInfo: {
    flex: 1,
    minWidth: 0,
  },
  indexName: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 2,
  },
  indexSymbol: {
    fontSize: 11,
    color: Colors.text.tertiary,
  },
  indexChartSection: {
    width: 60,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  indexPriceInfo: {
    alignItems: 'flex-end',
    minWidth: 70,
  },
  indexPrice: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 2,
  },
  indexChange: {
    fontSize: 12,
    fontWeight: '600',
  },
  marketCardsContainer: {
    paddingLeft: 24,
    paddingRight: 12,
  },
  moversSection: {
    marginBottom: 24,
  },
  moversHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 24,
    marginBottom: 16,
    gap: 8,
  },
  moversSubSection: {
    marginHorizontal: 24,
    marginBottom: 20,
  },
  moversSubHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 6,
  },
  moversSubTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.secondary,
  },
  bottomPadding: {
    height: 40,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    marginHorizontal: 24,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 14,
    color: Colors.text.secondary,
  },
  errorContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    paddingHorizontal: 40,
    marginHorizontal: 24,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text.primary,
    marginTop: 16,
  },
  errorMessage: {
    fontSize: 14,
    color: Colors.text.secondary,
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 20,
  },
  macroRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.background.tertiary,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  macroLeft: {
    width: 80,
  },
  macroSymbol: {
    color: Colors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  macroType: {
    color: Colors.text.tertiary,
    fontSize: 12,
  },
  macroCenter: {
    flex: 1,
    paddingHorizontal: 8,
  },
  macroNotes: {
    color: Colors.text.primary,
    fontSize: 13,
  },
  macroRight: {
    width: 140,
    alignItems: 'flex-end',
  },
  macroWhen: {
    color: Colors.text.secondary,
    fontSize: 12,
  },
  briefChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 8,
  },
  chip: {
    backgroundColor: 'rgba(59,130,246,0.1)',
    borderColor: Colors.ai.primary,
    borderWidth: 1,
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 999,
  },
  chipText: {
    color: Colors.ai.primary,
    fontSize: 12,
  },
  themeCard: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: Colors.background.tertiary,
  },
  themeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  themeTitle: {
    color: Colors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  themeMeta: {
    color: Colors.text.tertiary,
    fontSize: 12,
  },
  themeStory: {
    marginTop: 6,
    color: Colors.text.secondary,
    fontSize: 12,
  },
  exposureContainer: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: Colors.background.tertiary,
    marginHorizontal: 24,
    gap: 12,
  },
  exposureConnectedHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  exposureHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  exposureConnectedText: {
    color: Colors.text.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  exposureLastSyncCompact: {
    color: Colors.text.tertiary,
    fontSize: 10,
    marginBottom: 8,
  },
  refreshIconButton: {
    padding: 4,
  },
  disconnectButton: {
    backgroundColor: Colors.status.error,
  },
  exposureNote: {
    color: Colors.text.secondary,
    fontSize: 12,
    fontStyle: 'italic',
  },
  addConnectionContainer: {
    marginTop: 8,
    marginBottom: 8,
  },
  addConnectionButton: {
    backgroundColor: Colors.background.tertiary,
    paddingVertical: 8,
  },
  exposureText: {
    color: Colors.text.primary,
    fontSize: 14,
    flex: 1,
  },
  exposureCtaContainer: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: Colors.background.tertiary,
    marginHorizontal: 24,
  },
  exposureTitle: {
    color: Colors.text.primary,
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 6,
  },
  exposureSubtitle: {
    color: Colors.text.secondary,
    fontSize: 13,
    marginBottom: 12,
  },
  // Holdings styles
  holdingsLoadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    marginVertical: 8,
  },
  holdingsLoadingText: {
    color: Colors.text.secondary,
    fontSize: 13,
  },
  holdingsContainer: {
    marginVertical: 12,
    padding: 12,
    backgroundColor: Colors.background.secondary,
    borderRadius: 8,
  },
  holdingsTitle: {
    color: Colors.text.primary,
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 12,
  },
  positionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: Colors.background.tertiary,
    borderRadius: 8,
    backgroundColor: 'transparent',
  },
  positionRowPressed: {
    backgroundColor: Colors.background.tertiary,
    opacity: 0.7,
  },
  positionMain: {
    flex: 1,
  },
  positionSymbol: {
    color: Colors.text.primary,
    fontSize: 15,
    fontWeight: '700',
    marginBottom: 2,
  },
  positionName: {
    color: Colors.text.secondary,
    fontSize: 12,
  },
  positionDetails: {
    alignItems: 'flex-end',
  },
  positionQuantity: {
    color: Colors.text.primary,
    fontSize: 13,
    fontWeight: '600',
  },
  positionPrice: {
    color: Colors.text.tertiary,
    fontSize: 11,
    marginTop: 2,
  },
  holdingsEmptyContainer: {
    padding: 16,
    marginVertical: 8,
    backgroundColor: Colors.background.secondary,
    borderRadius: 8,
  },
  holdingsEmptyText: {
    color: Colors.text.secondary,
    fontSize: 13,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  comingSoonNote: {
    marginTop: 8,
    color: Colors.text.tertiary,
    fontSize: 12,
  },
  retryButton: {
    marginTop: 20,
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: Colors.accent.primary,
    borderRadius: 8,
  },
  retryButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  tokenBox: {
    backgroundColor: Colors.background.secondary,
    borderWidth: 1,
    borderColor: Colors.background.tertiary,
    borderRadius: 8,
    padding: 8,
  },
  tokenText: { color: Colors.text.secondary, fontSize: 12 },
  input: {
    backgroundColor: Colors.background.primary,
    color: Colors.text.primary,
    borderWidth: 1,
    borderColor: Colors.background.tertiary,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalCard: {
    width: '100%',
    maxWidth: 600,
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.background.tertiary,
    padding: 16,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  modalTitle: {
    color: Colors.text.primary,
    fontSize: 16,
    fontWeight: '700',
  },
  storyRow: {
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: Colors.background.tertiary,
    paddingHorizontal: 4,
  },
  storyTitle: {
    color: Colors.text.primary,
    fontSize: 14,
    marginBottom: 4,
  },
  storyMeta: {
    color: Colors.text.tertiary,
    fontSize: 12,
  },
});
